import { useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';

import { PendingInviteRoles } from '../components/PendingInviteRoles';
import type { PendingInviteWithGracePeriod } from '../utils/gracePeriod';
import { getMemberDisplayName } from '../utils/workspaceTeam';

import { EditMemberTray } from './EditMemberTray';

const messages = defineMessages({
  invitedLabel: {
    id: 'Team.pendingInviteTray.invitedLabel',
    defaultMessage: 'Invited {date}',
    description: 'Date the pending invite was sent, shown in the pending invite tray header',
  },
});

export type PendingInviteTrayProps = {
  /** One pending invite per legal entity, all for the same email. */
  invites: PendingInviteWithGracePeriod[];
  onClose: () => void;
  /** E.g. the "Cancel invite" button. Omit for a read-only tray. */
  footer?: ReactNode;
  now?: Date;
};

/** Read-only tray for a pending invite: who was invited, when, and the invited roles. */
export function PendingInviteTray({ invites, onClose, footer, now }: PendingInviteTrayProps) {
  const { formatDate, formatMessage } = useIntl();
  const trayRef = useRef<TrayRefProps>(null);

  const firstInvite = invites[0];
  const email = firstInvite?.email ?? '';
  const name = getMemberDisplayName(firstInvite ?? {});

  const joinedLabel = useMemo(() => {
    const createdAtTimes = invites
      .map((invite) => (invite.createdAt ? new Date(invite.createdAt).getTime() : NaN))
      .filter((time) => !Number.isNaN(time));
    if (createdAtTimes.length === 0) return undefined;
    return formatMessage(messages.invitedLabel, {
      date: formatDate(new Date(Math.min(...createdAtTimes)), { dateStyle: 'medium' }),
    });
  }, [formatDate, formatMessage, invites]);

  const handleBeforeClose = useCallback(() => {
    trayRef.current?.close();
  }, []);

  return (
    <EditMemberTray
      name={name}
      email={email}
      status="pending"
      joinedLabel={joinedLabel}
      footer={footer}
      trayRef={trayRef}
      beforeClose={handleBeforeClose}
      onClose={onClose}
    >
      <PendingInviteRoles invites={invites} now={now} />
    </EditMemberTray>
  );
}
